import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import type { Stripe, StripeElements } from "@stripe/stripe-js";
import PaymentConfirmationLayout from "../confirmation/PaymentConfirmationLayout";
import PaymentError from "../paymentError";
import { isAuthenticationPayment, CREDIT_CARD_FUNDING, CREDIT_CARDS_DISABLED_ERROR } from "../../utils/paymentHelpers";
import { getFeatureNameForAction } from "../../utils/featureSentiment";
import { formatDate, formatTime } from "../../../utils/countryLocaleMap";
import { useWidgetLocale } from "../hooks/useWidgetLocale";
import "../../styles/confirmation.scss";
import { AlertTriangle } from "lucide-react";
import { getBaseUrl } from "../../../common/jsonRpcRoutes";
import analytics from "../../analytics";
import { notifyBugsnag } from "../../bugsnag";
import { usePaymentRedirect } from "../context/PaymentRedirectContext";
import { buildPaymentReturnUrl } from "../../utils/paymentRedirectUrl";

interface CheckoutFormProps {
  paymentStatus: "idle" | "success" | "error";
  bookingData?: {
    depositTotal: number;
    guestCount: number;
    time: string;
    date: string;
    bookingId: string;
    venue: {
      title: string;
      currency: string;
      country_code: string | null;
      collins_features?: string[] | Record<string, unknown>;
    };
    paymentProcessor: string;
    depositTypePayment: string | boolean;
    /** The venue's deposit terms, repeated on the confirmation. */
    depositTerms?: string;
    bookingRef: string;
    deposits?: Array<string>;
    user?: {
      user_id: string;
      user_name: string;
    };
  };
  clientSecret: string;
  mode?: "full" | "successOnly" | "successWithDetails";
  amount?: number;
  action?: string;
  pendingBookingId?: string;
  /** Explicit booking ID — used as a fallback when bookingData.bookingId is not available. */
  bookingId?: string;
  /**
   * When true the booking type has credit card payments disabled, so a
   * credit-funded card is refused before the payment is confirmed.
   */
  disableCreditCards?: boolean;
}

/**
 * Tokenise the entered card without charging it so its funding type can be read,
 * mirroring how Collins refuses credit-funded cards before it creates a payment
 * intent. Returns true only when Stripe reports `credit` funding; a wallet or
 * other non-card method, `unknown` funding, or a failed lookup all return false
 * and are left to the Collins validation, which cancels the payment intent
 * before any money is taken.
 */
async function isCreditFundedCard(stripe: Stripe, elements: StripeElements): Promise<boolean> {
  try {
    const { confirmationToken, error } = await stripe.createConfirmationToken({ elements });

    if (error || !confirmationToken) {
      notifyBugsnag(new Error(error?.message ?? "Card funding lookup returned no confirmation token"), {
        component: "stripeConnectForm",
        event: "credit_card_funding_lookup_failed",
      });
      return false;
    }

    return confirmationToken.payment_method_preview?.card?.funding === CREDIT_CARD_FUNDING;
  } catch (err) {
    notifyBugsnag(err instanceof Error ? err : new Error(String(err)), {
      component: "stripeConnectForm",
      event: "credit_card_funding_lookup_failed",
    });
    return false;
  }
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  paymentStatus,
  bookingData,
  clientSecret,
  mode,
  amount,
  action,
  pendingBookingId,
  bookingId,
  disableCreditCards,
}) => {
  const { t } = useTranslation();
  const timeLocale = useWidgetLocale(bookingData?.venue.country_code);
  const stripe = useStripe();
  const elements = useElements();
  const { redirectUrl, signature } = usePaymentRedirect();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  // Stripe rejects elements.submit()/confirmPayment with an IntegrationError when the
  // Payment Element has not yet emitted its ready event, so submission stays blocked
  // until the element is mounted and ready.
  const [isElementReady, setIsElementReady] = useState(false);

  interface FinalisePayload {
    booking_id: string;
    action: string;
    pending_preorder_id?: string;
    payment_method_id?: string;
    setup_intent_id?: string;
    deposits?: string[];
  }

  const submitPayment = async () => {
    if (!stripe || !elements) return;

    // Submit the elements to validate the form
    const { error: submitError } = await elements.submit();
    if (submitError) {
      const errorMessage =
        submitError.message ?? t("payment_error_generic", "Something went wrong with your payment. Please try again.");
      setErrorMessages([errorMessage]);
      return;
    }

    // Refuse a credit-funded card before the payment is confirmed, so the guest is
    // told why in place rather than being sent to the confirmation page to find out.
    if (disableCreditCards && (await isCreditFundedCard(stripe, elements))) {
      setErrorMessages([t("credit_card_disabled_message", CREDIT_CARDS_DISABLED_ERROR)]);
      return;
    }

    // bookingData.bookingId is preferred; the explicit bookingId prop (passed from the
    // web component attribute) is used as a fallback for the embedded MFE where the
    // booking ID is not present in the page URL.
    const effectiveBookingId = bookingData?.bookingId ?? bookingId;
    // The journey this attempt belongs to: the pending booking on the initial /book
    // flow, whose id differs from the booking id the confirmation reports, and the
    // booking itself everywhere else.
    const paymentJourneyId = pendingBookingId ?? effectiveBookingId;

    // `payment_initiated` — validation has passed and confirmation with Stripe is
    // about to begin, so the outcome is not yet known. A retry raises it again.
    void analytics.paymentInitiated({
      id: effectiveBookingId,
      journeyId: paymentJourneyId,
      value: amount ?? bookingData?.depositTotal,
      currency: bookingData?.venue.currency,
      numPeople: bookingData?.guestCount,
    });

    const isPaymentIntent = clientSecret.startsWith("pi_");
    const isSetupIntent = clientSecret.startsWith("seti_");
    let error: any = undefined;
    const returnParams = new URLSearchParams(window.location.search);
    returnParams.set("action", action ?? "secure-your-booking");

    // Include booking_id in the return URL so ConfirmPayment can identify the booking.
    // However, exclude it when pending_booking_id is present to prevent signature validation issues
    // on /confirm page. The /book flow uses pending_booking_id instead of booking_id, and
    // SignatureGuard is designed to skip validation when both signature and booking_id are absent.
    // Also set pending_booking_id when applicable (e.g. during the initial booking flow),
    // which is what lets ConfirmPayment report the same journey on `purchase`.
    if (pendingBookingId) {
      // Explicitly remove any existing booking_id to prevent signature validation issues
      returnParams.delete("booking_id");
      returnParams.set("pending_booking_id", pendingBookingId);
    } else if (effectiveBookingId) {
      returnParams.set("booking_id", effectiveBookingId);
    }
    if (signature && !returnParams.has("signature")) {
      returnParams.set("signature", signature);
    }
    // When the MFE is embedded on a third-party page (base-url attribute set),
    // Stripe must redirect back to the embedding page so the MFE can detect
    // the returned payment params and render ConfirmPayment in-place.
    // In the standalone GC app (no base-url), use the standard /confirm/ route.
    const returnUrl = buildPaymentReturnUrl({
      redirectUrl,
      currentUrl: `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`,
      isEmbedded: Boolean(getBaseUrl()),
      returnParams,
    });

    if (isPaymentIntent) {
      const result = await stripe.confirmPayment({
        elements,
        redirect: "always",
        confirmParams: {
          return_url: returnUrl,
        },
      });

      error = result.error;
    } else if (isSetupIntent) {
      const result = await stripe.confirmSetup({
        elements,
        redirect: "always",
        confirmParams: {
          return_url: returnUrl,
        },
      });
      error = result.error;
    }

    // TO DO: After collins-api Klarna changes, check this again and add clearPreorders

    if (error) {
      const errorMessage =
        error.message || t("payment_error_generic", "Something went wrong with your payment. Please try again.");
      setErrorMessages([errorMessage]);
    }
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    let finalisePayload: FinalisePayload;
    if (!stripe || !elements || !isElementReady || isProcessing) return;

    setIsProcessing(true);
    setErrorMessages([]);

    try {
      await submitPayment();
    } catch (err) {
      notifyBugsnag(err instanceof Error ? err : new Error(String(err)), {
        component: "stripeConnectForm",
        action,
        bookingId: bookingData?.bookingId ?? bookingId,
      });
      setErrorMessages([t("payment_error_generic", "Something went wrong with your payment. Please try again.")]);
    }

    setIsProcessing(false);
  };

  const paymentAmexEnabled = (() => {
    const features = bookingData?.venue?.collins_features;
    if (Array.isArray(features)) {
      return features.includes("payment_amex_enabled") ? true : undefined;
    }
    if (features && typeof features === "object") {
      return features.payment_amex_enabled;
    }
    return undefined;
  })();

  return (
    <form id="gc-stripe-payment-form" className="gcw:flex gcw:flex-col gcw:items-center" onSubmit={handleSubmit}>
      {paymentStatus !== "success" && paymentAmexEnabled === false && (
        <output className="amex-info-notice gcw:mb-6 gcw:w-full gcw:block">
          <div className="gcw:border gcw:border-gray-300 gcw:bg-gray-50 gcw:rounded-lg gcw:p-4 gcw:flex gcw:gap-3">
            <AlertTriangle className="gcw:h-6 gcw:w-6 gcw:text-gray-500 gcw:flex-shrink-0 gcw:mt-0.5" />
            <p className="gcw:text-gray-900 gcw:flex-1">
              {t(
                "amex_not_supported_notice",
                "Note: American Express (AMEX) cards are not supported. Please use a Visa or Mastercard.",
              )}
            </p>
          </div>
        </output>
      )}
      {paymentStatus !== "success" && <PaymentElement onReady={() => setIsElementReady(true)} />}
      {paymentStatus !== "success" && (
        <button
          className="gcw:inline-flex gcw:min-h-12 gcw:items-center gcw:justify-center gcw:rounded-md gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:text-lg gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 pay-button gcw:mt-bs-3"
          type="submit"
          disabled={!stripe || !isElementReady || isProcessing}
        >
          {isProcessing ? "Processing..." : "Pay"}
        </button>
      )}
      {paymentStatus === "success" && bookingData && (
        <PaymentConfirmationLayout
          venueName={bookingData.venue.title}
          bookingRef={bookingData.bookingRef}
          depositTotal={bookingData.depositTotal}
          isAuthentication={isAuthenticationPayment(bookingData.depositTypePayment)}
          partySize={bookingData.guestCount}
          date={
            bookingData.date && typeof bookingData.date === "string"
              ? formatDate(bookingData.date, bookingData.venue.country_code ?? "GB", "short")
              : ""
          }
          time={
            bookingData.time && bookingData.date && typeof bookingData.date === "string"
              ? formatTime(bookingData.time, bookingData.date, bookingData.venue.country_code ?? "GB", timeLocale)
              : bookingData.time
          }
          location={bookingData.venue.title}
          preOrderItems={undefined}
          currency={bookingData.venue.currency}
          countryCode={bookingData.venue.country_code}
          mode={mode}
          featureName={getFeatureNameForAction(action)}
          userId={bookingData.user?.user_id ?? null}
          customerName={bookingData.user?.user_name ?? null}
          depositTerms={bookingData.depositTerms}
        />
      )}
      {errorMessages.length > 0 && <PaymentError errorMessages={errorMessages} />}
    </form>
  );
};

export default CheckoutForm;
